import { useConfigStore, useFacilityStore } from '@/stores';
import styles from './styles.module.scss';
import ShopFnbFacilityCategoryButton from '@/components/common/shop-fnb-facility/buttons/category';
import { useFacilityCategories } from '@/hooks/poi/useFacilityData';
import type { BuildingCategory } from '@/services/dto';
import { useTranslation } from 'react-i18next';
import { FACILITY_SEARCH_MODE } from '@/enums/facility';
import { getCategoryLocalizedName } from '@/utils/poi';
import { useOrientation } from '@/hooks/useOrientation';
import clsx from 'clsx';

const getCategoryLabel = (category: BuildingCategory, language: string, t: (key: string) => string): string => {
    if (category.clientCode === 'all') {
        const name = category.name;
        if (typeof name === 'string') return t(name);
        if (Array.isArray(name)) return getCategoryLocalizedName(category, language);
        return t(name[language]);
    }
    return getCategoryLocalizedName(category, language);
};

interface FacilitySearchByCategoryProps {
    onSelectCategory?: (category: BuildingCategory) => void;
}

const FacilitySearchByCategory = ({ onSelectCategory }: FacilitySearchByCategoryProps) => {
    const language = useConfigStore((state) => state.config.ui.language);
    const { isHorizontal } = useOrientation();

    const { t } = useTranslation();
    const selectedCategory = useFacilityStore((state) => state.selectedCategory);
    const setSelectedCategory = useFacilityStore((state) => state.setSelectedCategory);
    const updateFacilityContents = useFacilityStore((state) => state.updateFacilityContents);
    const facilityCategories = useFacilityCategories();

    const handleSelectCategory = (category: BuildingCategory) => {
        setSelectedCategory(category);
        onSelectCategory?.(category);
        updateFacilityContents(FACILITY_SEARCH_MODE.SEARCH_BY_CATEGORY);
    };

    return (
        <div
            data-region-name="fnb-search-by-category"
            className={clsx(styles['fnb-search-by-category'], {
                [styles['fnb-search-by-category--vertical']]: !isHorizontal,
            })}
            data-region-nav
        >
            {facilityCategories.map((category) => {
                const categoryLabel = getCategoryLabel(category, language, t);

                return (
                    <ShopFnbFacilityCategoryButton
                        tabIndex={9}
                        data-interactive
                        role="radio"
                        type="button"
                        aria-checked={selectedCategory?.clientCode === category.clientCode}
                        aria-label={categoryLabel}
                        key={category.clientCode}
                        title={categoryLabel}
                        onClick={() => handleSelectCategory(category)}
                    />
                );
            })}
        </div>
    );
};

export default FacilitySearchByCategory;
